import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Sidebar switcher slot — the mirror image of HeaderActionsSlot.
 *
 * <p>The sidebar reserves a DOM node; the HEADER renders the switcher into it. The direction matters:
 * the org switcher's props come from a large amount of header-local state (org catalogue, name map,
 * accessibility, and a `handleOrgChange` that resets game/client, persists the active context and
 * navigates off URL-pinned workspace routes). Publishing all of that across a context boundary would
 * duplicate the wiring — or re-publish an object every render and loop. Portalling the header's own
 * element instead keeps one owner and one handler, and the sidebar just supplies the location.
 *
 *   <SidebarSwitcherSlotProvider>     ← mount once (DashboardLayout)
 *     <SidebarSwitcherSlot />         ← the portal target (DashboardSidebar)
 *     <SidebarSwitcher>               ← the content (DashboardHeader)
 *       <ContextSwitcher ... />
 *     </SidebarSwitcher>
 *   </SidebarSwitcherSlotProvider>
 */

const SidebarSwitcherContext = createContext(null);

export const SidebarSwitcherSlotProvider = ({ children }) => {
    const [slotNode, setSlotNode] = useState(null);
    return (
        <SidebarSwitcherContext.Provider value={{ slotNode, setSlotNode }}>
            {children}
        </SidebarSwitcherContext.Provider>
    );
};

// The node the sidebar reserves. Renders a plain div when the provider is missing (isolated tests).
export const SidebarSwitcherSlot = ({ className, ...rest }) => {
    const ctx = useContext(SidebarSwitcherContext);
    if (!ctx) return <div className={className} {...rest} />;
    return <div ref={ctx.setSlotNode} className={className} {...rest} />;
};

// Used by the header to place its switcher into the sidebar. Renders nothing until the slot mounts.
export const SidebarSwitcher = ({ children }) => {
    const ctx = useContext(SidebarSwitcherContext);
    if (!ctx || !ctx.slotNode) return null;
    return createPortal(children, ctx.slotNode);
};
